import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Mail } from "lucide-react";
import { useAuthStore } from "../../stores/auth";
import { AuthLayout } from "../../components/layout/AuthLayout";
import { Input } from "../../components/ui/Input";
import { PasswordInput } from "../../components/ui/PasswordInput";
import { Button } from "../../components/ui/Button";

export function LoginPage() {
  const login = useAuthStore((state) => state.login);
  const error = useAuthStore((state) => state.error);
  const clearError = useAuthStore((state) => state.clearError);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);

  const from =
    (location.state as { from?: string } | null)?.from ?? "/app/dashboard";

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch {
      // Error rendered from the store.
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout>
      <h1>Welcome back</h1>
      <p className="auth-card__subtitle">
        Sign in to your Zirro account to continue building.
      </p>

      <form className="auth-form" onSubmit={onSubmit}>
        <Input
          label="Email address"
          type="email"
          name="email"
          autoComplete="email"
          required
          placeholder="you@example.com"
          icon={<Mail size={16} />}
          value={email}
          error={error && !password ? error : undefined}
          onChange={(event) => {
            clearError();
            setEmail(event.target.value);
          }}
        />

        <PasswordInput
          label="Password"
          name="password"
          autoComplete="current-password"
          required
          placeholder="Enter your password"
          value={password}
          onChange={(event) => {
            clearError();
            setPassword(event.target.value);
          }}
        />

        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}

        <Button type="submit" loading={pending} fullWidth size="lg">
          Sign in
        </Button>
      </form>

      <div className="auth-card__footer">
        <p>
          <Link to="/forgot-password">Forgot your password?</Link>
        </p>
        <p style={{ marginTop: "var(--z-space-3)" }}>
          Don't have an account?{" "}
          <Link to="/signup" state={location.state}>Create one free</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
